// Rebuy product grouping — metafield-based (custom.product_family)
// custom.product_family is a list.product_reference metafield that holds the
// GIDs of every product in the color group. We extract numeric IDs from those
// GIDs, pull full product data from Rebuy's static API, and stitch variants +
// images onto the widget product so the custom swatch block drives cross-product
// selection. Swatch images are resolved from the shopify.color-pattern metaobject
// via a single batched Storefront API call per widget render.

class RebuyClient {
  constructor() {
    this.maxCacheMinutes = 30;
    this.storageKey = 'productJSON';
  }

  // Read the API key lazily so it's available after Rebuy initializes
  get clientToken() {
    return window.Rebuy?.shop?.api_key;
  }

  async query(url, params) {
    const key = this.clientToken;
    if (!key) {
      console.warn('[rebuy-grouping] Rebuy API key not available yet');
      return null;
    }
    return fetch(
      `https://rebuyengine.com/${url}?${new URLSearchParams({ key, ...params })}`,
      { method: 'GET', headers: { 'Content-type': 'application/json; charset=UTF-8' } }
    ).then((res) => res.json());
  }

  async getProductsByIDs(productIDs) {
    const products = [];
    const uncachedIDs = [];

    productIDs.forEach((id) => {
      const cached = this._readCache(id);
      if (cached) products.push(cached);
      else uncachedIDs.push(id);
    });

    if (uncachedIDs.length) {
      const response = await this.query('api/v1/products/static', {
        metafields: 'yes',
        shopify_product_ids: uncachedIDs.join(','),
        limit: 100,
      });
      if (!response?.data) {
        console.warn('[rebuy-grouping] static API returned no data:', response);
        return products;
      }
      response.data.forEach((p) => {
        this._writeCache(p.id, p);
        products.push(p);
      });
    }

    return products.sort((a, b) => productIDs.indexOf(String(a.id)) - productIDs.indexOf(String(b.id)));
  }

  _readCache(key) {
    const cacheMax = Date.now() - this.maxCacheMinutes * 60 * 1000;
    try {
      const parsed = JSON.parse(localStorage.getItem(`${this.storageKey}:${key}`));
      return parsed && parsed.cachedAt > cacheMax ? parsed : null;
    } catch (_) { return null; }
  }

  _writeCache(key, value) {
    value.cachedAt = Date.now();
    this._safeCacheWrite(`${this.storageKey}:${key}`, JSON.stringify(value));
  }

  _safeCacheWrite(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      if (e.name === 'QuotaExceededError') {
        this._evictOldestEntries();
        try { localStorage.setItem(key, value); } catch (_) {}
      }
    }
  }

  _evictOldestEntries() {
    const entries = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key.startsWith('productJSON:')) {
        try {
          const parsed = JSON.parse(localStorage.getItem(key));
          entries.push({ key, cachedAt: parsed?.cachedAt || 0 });
        } catch (_) { entries.push({ key, cachedAt: 0 }); }
      }
    }
    entries.sort((a, b) => a.cachedAt - b.cachedAt);
    entries.slice(0, Math.max(1, Math.ceil(entries.length / 2))).forEach((e) =>
      localStorage.removeItem(e.key)
    );
  }
}

const rebuyClient = new RebuyClient();

// In-memory cache for resolved metaobject data — avoids duplicate Storefront API calls within a page session.
const metaobjectCache = {};

// Fetches shopify.color-pattern metaobject GIDs from the Storefront API and returns
// a map of GID → { image, color }. Results are cached for the page session.
const resolveColorPatternMetaobjects = async (gids) => {
  const uncachedGids = gids.filter((gid) => !(gid in metaobjectCache));

  if (uncachedGids.length) {
    const shop = window.Shopify?.shop || window.location.hostname;
    const query = `query GetColorPatterns($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on Metaobject {
          id
          fields {
            key
            value
            reference {
              ... on MediaImage { image { url } }
              ... on Product { handle }
            }
          }
        }
      }
    }`;
    try {
      const res = await fetch(`https://${shop}/api/2024-10/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': '9adf781467e355e6783cbb004ce2b6ac',
        },
        body: JSON.stringify({ query, variables: { ids: uncachedGids } }),
      });
      const data = await res.json();
      (data?.data?.nodes || []).forEach((node) => {
        if (!node?.id) return;
        let image = null;
        let color = null;
        let label = null;
        let productHandle = null;
        (node.fields || []).forEach((field) => {
          if (field.reference?.image?.url) image = field.reference.image.url;
          if (field.reference?.handle) productHandle = field.reference.handle;
          if (field.key === 'color' && field.value) color = field.value;
          if ((field.key === 'label' || field.key === 'name') && field.value) label = field.value;
        });
        metaobjectCache[node.id] = { image, color, label, productHandle };
      });
    } catch (e) {
      console.warn('[rebuy-grouping] Failed to resolve color-pattern metaobjects:', e);
    }
    // Mark any GIDs that didn't come back so we don't retry them
    uncachedGids.forEach((gid) => { if (!(gid in metaobjectCache)) metaobjectCache[gid] = null; });
  }

  return Object.fromEntries(gids.map((gid) => [gid, metaobjectCache[gid] || null]));
};

// Returns the inline style object for a grouping swatch element.
// Called from the widget Vue template to keep the binding concise.
window.rebuySwatchStyle = (productId, value) => {
  const s = window.rebuyGroupingSwatches?.[productId]?.[value];
  if (!s) return {};
  if (s.image) return { backgroundImage: `url(${s.image})`, backgroundSize: 'cover', backgroundPosition: 'center' };
  if (s.color) return { backgroundColor: s.color };
  return {};
};

// Reads the list.product_reference GIDs from custom.product_family and returns
// numeric Shopify product IDs, or null if the metafield is absent.
const getProductFamilyIds = (product) => {
  let value = product.metafields?.custom?.product_family;
  if (!value) return null;
  // Rebuy returns list.product_reference metafields as a JSON-encoded string
  if (typeof value === 'string') {
    try { value = JSON.parse(value); } catch (_) {}
  }
  const gids = Array.isArray(value) ? value : [value];
  const ids = gids
    .map((gid) => {
      const match = String(gid).match(/\/(\d+)$/);
      return match ? match[1] : null;
    })
    .filter(Boolean);
  return ids.length ? ids : null;
};

const productHasGrouping = (product) => !!getProductFamilyIds(product);

// Builds swatch data for products that have shopify.color-pattern but no product_family.
// Resolves each metaobject GID for image/color, and cross-references gidToProduct
// (built from widget siblings) for the redirect URL — no extra API call needed.
const buildSwatchesFromColorPattern = async (product, gidToProduct) => {
  let gids = product.metafields?.shopify?.['color-pattern'] ?? product.metafields?.shopify?.color_pattern;
  if (typeof gids === 'string') gids = [gids];
  if (!Array.isArray(gids) || !gids.length) return;

  const metaobjectMap = await resolveColorPatternMetaobjects(gids);

  const swatchData = {};
  gids.forEach((gid) => {
    const resolved = metaobjectMap[gid];
    if (!resolved) return;
    const sibling = gidToProduct[gid];
    const label = resolved.label || gid;
    swatchData[label] = {
      image: resolved.image || null,
      color: resolved.color || null,
      url: sibling?.handle ? `/products/${sibling.handle}` : (resolved.productHandle ? `/products/${resolved.productHandle}` : null),
    };
  });

  if (Object.keys(swatchData).length) {
    window.rebuyGroupingSwatches = window.rebuyGroupingSwatches || {};
    window.rebuyGroupingSwatches[product.id] = swatchData;
  }
};

// Merges variants and images from all products in the family onto the seed product.
// Creates a synthetic Color option when none exists, using each product's
// first option value as the color label (e.g. "Black", "White").
const stitchRebuyProductsToWidget = async (product, idsArray) => {
  if (!idsArray.length) return;

  const rebuyProducts = await rebuyClient.getProductsByIDs(idsArray);

  if (!rebuyProducts.length) return;

  product.images = rebuyProducts.flatMap((p) =>
    p.images.map((img, i) => ({ ...img, position: i }))
  );

  // Use product title as option1 — grouped products are single-variant so option1
  // would be "Default Title" without this override, which breaks swatch labels.
  product.variants = rebuyProducts.flatMap((p) =>
    p.variants.map((v) => ({
      ...v,
      option1: p.title,
      title: p.title,
      image_id: p.image?.id,
      image_url: p.image?.src,
    }))
  );

  // Find the color option by name, or create one if the products are single-variant
  let colorOption = product.options?.find((o) => o.name.toLowerCase() === 'color');
  if (!colorOption) {
    colorOption = { name: 'Color', values: [] };
    if (!product.options) product.options = [];
    product.options.unshift(colorOption);
  }

  // Current product's swatch first, then the rest in family order
  const otherTitles = rebuyProducts.map((p) => p.title).filter((t) => t !== product.title);
  colorOption.values = [product.title, ...otherTitles];

  // Collect the first shopify.color-pattern GID per product and batch-resolve via Storefront API.
  // Falls back to the last product image if the metafield is absent or unresolvable.
  const allGids = [];
  const productFirstGid = {};
  rebuyProducts.forEach((p) => {
    let gids = p.metafields?.shopify?.['color-pattern'] ?? p.metafields?.shopify?.color_pattern;
    if (typeof gids === 'string') gids = [gids];
    if (Array.isArray(gids) && gids.length) {
      const firstGid = gids[0];
      productFirstGid[p.id] = firstGid;
      if (!allGids.includes(firstGid)) allGids.push(firstGid);
    }
  });

  const metaobjectMap = allGids.length ? await resolveColorPatternMetaobjects(allGids) : {};

  const swatchData = {};
  rebuyProducts.forEach((p) => {
    const resolved = productFirstGid[p.id] ? metaobjectMap[productFirstGid[p.id]] : null;
    swatchData[p.title] = {
      image: resolved?.image || p.images?.[p.images.length - 1]?.src || null,
      color: resolved?.color || null,
      url: p.handle ? `/products/${p.handle}` : null,
    };
  });
  // Store in a window global keyed by product ID — avoids Vue 2 reactivity limitations
  // when adding new properties to already-observed objects post-render.
  window.rebuyGroupingSwatches = window.rebuyGroupingSwatches || {};
  window.rebuyGroupingSwatches[product.id] = swatchData;
};

const stitchProductsToWidget = async (product, familyLookup, gidToProduct) => {
  // Use the product's own metafield first; fall back to lookup built from sibling products
  const ids = getProductFamilyIds(product) || familyLookup[String(product.id)] || null;

  if (ids) {
    await stitchRebuyProductsToWidget(product, ids);
    product.productGrouping = ids;
  } else {
    product.productGrouping = [];
    await buildSwatchesFromColorPattern(product, gidToProduct);
  }
};

document.addEventListener('rebuy.productsChange', async (event) => {
  const widget = event.detail.widget;
  if (Number(widget?.id) !== 291354) return;
  if (!widget?.data?.products) return;

  const products = widget.data.products;

  // Pass 1: build product_family lookup for sibling products that lack the metafield.
  const familyLookup = {};
  products.forEach((p) => {
    const ids = getProductFamilyIds(p);
    if (ids) {
      ids.forEach((id) => { familyLookup[String(id)] = ids; });
    }
  });

  // Pass 2: build a map of first color-pattern GID → product so swatches can resolve
  // redirect URLs without an extra API call. Each product's first GID is its own color.
  const gidToProduct = {};
  products.forEach((p) => {
    let gids = p.metafields?.shopify?.['color-pattern'] ?? p.metafields?.shopify?.color_pattern;
    if (typeof gids === 'string') gids = [gids];
    if (Array.isArray(gids) && gids.length && !gidToProduct[gids[0]]) {
      gidToProduct[gids[0]] = { handle: p.handle, title: p.title };
    }
  });

  await Promise.all(products.map((p) => stitchProductsToWidget(p, familyLookup, gidToProduct)));

  widget?.View?.vue?.$forceUpdate();
});
